import React, { useState, useEffect } from 'react'
import { Dispatch } from 'redux'
import { connect } from 'react-redux'
import { match } from 'react-router-dom'
import withPermissionCheck from '../../core/access-control/with-permission-check'
import { Permission } from '../../core/auth0'

import RootState from '../../core/root-state'
import Layout from '../layouts/layout'
import Wrapper from '../layouts/wrapper'
import {
  Food,
  foodsActions,
  FoodImageType,
  FoodImage,
  FoodCategory,
} from '../../core/foods'
import { LANGUAGE_CODES, SupportedLanguageCode } from '../../core/i18n'
import FoodInfoTable from '../components/each-food/food-info-table'
import { strings } from '../components/each-food/each-food-content'
import styles from '../styles/modules/each-food/each-food.module.scss'
import FoodInfoForm from '../components/each-food/food-info-form'
import Button from '../components/button'
import PageLanguageTabs from '../components/common/page-language-tabs'
import FoodImageForms from '../components/each-food/food-image-forms'
import FoodImageItems from '../components/each-food/food-image-items'

interface Props {
  match: match<any>
  currentFood: Food | null
  foodImage: FoodImage
  foodCategories: FoodCategory[]
  fetchFood: (foodId: number) => void
  setFood: (id: number, food: Food) => void
  uploadFoodImage: (
    id: number,
    foodImageType: FoodImageType,
    file: File
  ) => void
  fetchFoodCategories: (brandCode: string) => void
}

const EachFoodScreen: React.FC<Props> = ({
  match,
  currentFood,
  foodImage,
  foodCategories,
  fetchFood,
  setFood,
  uploadFoodImage,
  fetchFoodCategories,
}) => {
  const [isEditing, setIsEditing] = useState(false)
  const [foodEditing, setFoodEditing] = useState<Food | null>(null)
  const [pageActiveLanguage, setPageActiveLanguage] = useState<
    SupportedLanguageCode
  >(LANGUAGE_CODES.JAPANESE)

  const id = Number(match.params.id)
  const food = currentFood

  useEffect(() => {
    fetchFood(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  useEffect(() => {
    if (food && food.brandCode) {
      fetchFoodCategories(food.brandCode)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [food])

  useEffect(() => {
    if (foodImage && foodEditing) {
      if (foodImage.foodImageType === FoodImageType.ThumbnailImage) {
        // 現在の値と異なる場合のみ更新
        if (foodEditing.thumbnailImageFileName !== foodImage.fileName) {
          setFoodEditing(prevFood => ({
            ...prevFood,
            thumbnailImageFileName: foodImage.fileName,
          }))
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [foodImage])

  const isSavable = () => {
    if (!foodEditing) {
      return false
    }

    const { name, price, priceExcludingTax, thumbnailImageFileName } = foodEditing

    // 必須フィールドのチェック
    if (!name || name.trim() === '') {
      return false
    }

    if (price === null || price === undefined || price === 0) {
      return false
    }

    if (priceExcludingTax === null || priceExcludingTax === undefined || priceExcludingTax === 0) {
      return false
    }

    if (!thumbnailImageFileName || thumbnailImageFileName.trim() === '') {
      return false
    }

    return true
  }

  const handleOnClickEdit = (food: Food) => {
    setIsEditing(true)
    setFoodEditing(food)
  }

  const handleOnClickQuit = () => {
    setIsEditing(false)
    setFoodEditing(null)
    setPageActiveLanguage(LANGUAGE_CODES.JAPANESE)
  }

  const handleOnClickSave = (id: number) => {
    if (foodEditing) {
      setFood(id, foodEditing)
      setIsEditing(false)
      setFoodEditing(null)
      setPageActiveLanguage(LANGUAGE_CODES.JAPANESE)
    }
  }

  const handleOnChangeFoodInfo = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    if (!foodEditing) {
      return
    }

    const name = event.target.name
    const value = event.target.value

    if (name.startsWith('foodTranslations.')) {
      const [, language, field] = name.split('.')
      const foodTranslations = [...foodEditing.foodTranslations]
      const langIndex = foodTranslations.findIndex(t => t.langCode === language)

      if (langIndex >= 0) {
        foodTranslations[langIndex] = {
          ...foodTranslations[langIndex],
          [field]: value,
        }
      } else {
        const newTranslation: any = {
          langCode: language,
          name: '',
        }
        newTranslation[field] = value
        foodTranslations.push(newTranslation)
      }

      setFoodEditing({
        ...foodEditing,
        foodTranslations,
      })
    } else if (name === 'name' || name === 'imageURL' || name === 'category') {
      if (name === 'imageURL') {
        setFoodEditing({ ...foodEditing, thumbnailImageFileName: value })
      } else {
        setFoodEditing({ ...foodEditing, [name]: value })
      }
    } else if (name === 'price' || name === 'priceExcludingTax') {
      const re = /^[0-9\b]+$/
      if (value === '') {
        setFoodEditing({ ...foodEditing, [name]: null })
      }
      if (re.test(value)) {
        setFoodEditing({ ...foodEditing, [name]: Number(value) })
      }
    }
  }

  const handleOnPageLanguageChange = (language: SupportedLanguageCode) => {
    setPageActiveLanguage(language)
  }

  return (
    <Layout location={'foods'}>
      <Wrapper title={`ID: ${id} ${food ? food.name : ''}`}>
        {food && (
          <>
            {isEditing ? (
              <>
                <Button
                  className={styles.foodEditButton}
                  title={strings.quit}
                  onClick={handleOnClickQuit}
                />
                <Button
                  className={styles.foodEditButton}
                  title={strings.save}
                  disabled={!isSavable()}
                  onClick={() => handleOnClickSave(id)}
                />
                <PageLanguageTabs
                  activeLanguage={pageActiveLanguage}
                  onLanguageChange={handleOnPageLanguageChange}
                />
                <h3 className={styles.title}>{strings.foodInfo}</h3>
                <FoodInfoForm
                  food={foodEditing}
                  activeLanguage={pageActiveLanguage}
                  categories={foodCategories}
                  onChange={handleOnChangeFoodInfo}
                />
                <h3 className={styles.title}>画像</h3>
                <FoodImageForms
                  food={foodEditing}
                  foodImage={foodImage}
                  id={id}
                  uploadFoodImage={uploadFoodImage}
                />
              </>
            ) : (
              <>
                <Button
                  className={styles.foodEditButton}
                  title={strings.edit}
                  onClick={() => handleOnClickEdit(food)}
                />
                <PageLanguageTabs
                  activeLanguage={pageActiveLanguage}
                  onLanguageChange={handleOnPageLanguageChange}
                />
                <h3 className={styles.title}>{strings.foodInfo}</h3>
                <FoodInfoTable
                  food={food}
                  activeLanguage={pageActiveLanguage}
                />
                <h3 className={styles.title}>画像</h3>
                <FoodImageItems food={food} />
              </>
            )}
          </>
        )}
      </Wrapper>
    </Layout>
  )
}

const mapStateToProps = (state: RootState) => ({
  currentFood: state.foods.currentFood,
  foodImage: state.foods.foodImage,
  foodCategories: state.foods.foodCategories,
})

const mapDispatchToProps = (dispatch: Dispatch) => ({
  fetchFood: (foodId: number) => dispatch(foodsActions.fetchFood(foodId)),
  setFood: (id: number, food: Food) => dispatch(foodsActions.setFood(id, food)),
  uploadFoodImage: (id: number, foodImageType: FoodImageType, file: File) =>
    dispatch(foodsActions.uploadFoodImage(id, foodImageType, file)),
  fetchFoodCategories: (brandCode: string) =>
    dispatch(foodsActions.fetchFoodCategories(brandCode)),
})

const ConnectedComponent = connect(
  mapStateToProps,
  mapDispatchToProps
)(EachFoodScreen)

export default withPermissionCheck(ConnectedComponent, {
  requiredPermission: Permission.FOOD_READ,
  redirectTo: '/404',
})
